import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Boxes, Plus, Pencil, Trash2, Clock, BatteryCharging, Layers, List as ListIcon, FolderTree, LayoutGrid, ChevronDown, ChevronRight, Radar } from "lucide-react";
import { DroneIcon } from "@/components/DroneIcon";
import { MultiImageUploader } from "@/components/MultiImageUploader";

const CATEGORIES = ["drone", "battery", "camera", "sensor", "vehicle", "generator", "charger", "other"];
const ASSET_TYPES = ["Drone", "Helicopter", "Aeroplane", "Vehicle", "Trailer", "Plant", "Equipment", "Misc"];
const STATUSES = ["available", "in_use", "maintenance", "retired"];
const STATUS_STYLE = {
  available: "bg-lime-50 text-lime-700 border-lime-200",
  in_use: "bg-blue-50 text-blue-700 border-blue-200",
  maintenance: "bg-amber-50 text-amber-700 border-amber-200",
  retired: "bg-slate-100 text-slate-500 border-slate-200",
};

const empty = { name: "", category: "drone", asset_type: "Drone", model: "", serial_no: "", registration: "", icao24: "", status: "available", purchase_date: "", flight_hours: 0, battery_count: 0, notes: "", image_path: null, images: [], group_id: null, maintenance_interval_hours: 0, maintenance_interval_days: 0, last_service_date: "", last_service_hours: 0, cycles: 0, airworthiness_expiry: "", maintenance_controller: "", wet_rate: "", dry_rate: "", student_rate: "", landing_fee: "" };
const emptyGroup = { name: "", description: "", image_path: null };

export default function AssetsPage() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [scope, setScope] = useState("assets"); // assets | groups
  const [layout, setLayout] = useState("list"); // list | tiles
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [savingGroup, setSavingGroup] = useState(false);

  const load = useCallback(async () => {
    const [a, g] = await Promise.all([api.get("/assets"), api.get("/asset-groups")]);
    setAssets(a.data); setGroups(g.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (a) => { setForm({ ...empty, ...a }); setOpen(true); };

  const setG = (k, v) => setGroupForm((f) => ({ ...f, [k]: v }));
  const openNewGroup = () => { setGroupForm(emptyGroup); setGroupOpen(true); };
  const openEditGroup = (g) => { setGroupForm({ ...emptyGroup, ...g }); setGroupOpen(true); };

  const saveGroup = async () => {
    if (!groupForm.name) { toast.error("Group name is required"); return; }
    setSavingGroup(true);
    const payload = { name: groupForm.name, description: groupForm.description, image_path: groupForm.image_path };
    try {
      if (groupForm.id) await api.put(`/asset-groups/${groupForm.id}`, payload);
      else await api.post("/asset-groups", payload);
      toast.success("Group saved"); setGroupOpen(false); load();
    } catch { toast.error("Save failed"); } finally { setSavingGroup(false); }
  };
  const removeGroup = async (g) => {
    try { await api.delete(`/asset-groups/${g.id}`); toast.success("Group deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_path", r.data.path);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    const num = (v) => (v === "" || v == null ? null : +v);
    const payload = { ...form, flight_hours: +form.flight_hours || 0, battery_count: +form.battery_count || 0, purchase_date: form.purchase_date || null, maintenance_interval_hours: +form.maintenance_interval_hours || 0, maintenance_interval_days: +form.maintenance_interval_days || 0, last_service_hours: +form.last_service_hours || 0, cycles: +form.cycles || 0, wet_rate: num(form.wet_rate), dry_rate: num(form.dry_rate), student_rate: num(form.student_rate), landing_fee: num(form.landing_fee) };
    delete payload.id; delete payload.created_at;
    try {
      if (form.id) await api.put(`/assets/${form.id}`, payload);
      else await api.post("/assets", payload);
      toast.success("Asset saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    try { await api.delete(`/assets/${a.id}`); toast.success("Asset deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const imgUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;

  const groupUpload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setG("image_path", r.data.path); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  const renderCard = (a) => (
    <div key={a.id} data-testid={`asset-card-${a.id}`} onClick={() => navigate(`/assets/${a.id}`)} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-all cursor-pointer">
      <div className="h-36 bg-slate-100 flex items-center justify-center overflow-hidden">
        {a.image_path
          ? <img src={imgUrl(a.image_path)} alt={a.name} className="h-full w-full object-cover" />
          : <DroneIcon className="h-12 w-12 text-slate-300" />}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-slate-900 truncate">{a.name}</h3>
            <p className="text-xs text-slate-500 truncate">{a.model || a.category}</p>
          </div>
          <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLE[a.status]}`}>{a.status.replace("_", " ")}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          {a.registration && <span>Reg: {a.registration}</span>}
          {a.serial_no && <span>S/N: {a.serial_no}</span>}
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.flight_hours || 0}h</span>
          <span className="inline-flex items-center gap-1"><BatteryCharging className="h-3 w-3" /> {a.battery_count || 0}</span>
        </div>
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant="outline" data-testid={`edit-asset-${a.id}`} onClick={(e) => { e.stopPropagation(); openEdit(a); }} className="flex-1"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
          <Button size="sm" variant="outline" data-testid={`delete-asset-${a.id}`} onClick={(e) => { e.stopPropagation(); remove(a); }} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </div>
  );

  const renderRow = (a) => (
    <div key={a.id} data-testid={`asset-row-${a.id}`} onClick={() => navigate(`/assets/${a.id}`)} className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:shadow-md hover:border-slate-300 transition-all cursor-pointer">
      <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
        {a.image_path ? <img src={imgUrl(a.image_path)} alt={a.name} className="h-full w-full object-cover" /> : <DroneIcon className="h-6 w-6 text-slate-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-heading font-semibold text-slate-900 truncate">{a.name}</h3>
        <p className="text-xs text-slate-500 truncate capitalize">{a.category}{a.model ? ` · ${a.model}` : ""}{a.registration ? ` · ${a.registration}` : ""}</p>
      </div>
      <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500 shrink-0"><Clock className="h-3 w-3" /> {a.flight_hours || 0}h</span>
      <span className={`hidden sm:inline shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLE[a.status]}`}>{a.status.replace("_", " ")}</span>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" data-testid={`edit-asset-${a.id}`} onClick={(e) => { e.stopPropagation(); openEdit(a); }}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`delete-asset-${a.id}`} onClick={(e) => { e.stopPropagation(); remove(a); }}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );

  const grouped = groups.map((g) => ({ group: g, items: assets.filter((a) => a.group_id === g.id) }));
  const ungrouped = assets.filter((a) => !a.group_id || !groups.some((g) => g.id === a.group_id));
  const allSections = [...grouped, { group: { id: "__ungrouped", name: "Ungrouped", description: "Assets not assigned to a kit" }, items: ungrouped, ungrouped: true }];

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Fleet & Equipment</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <Boxes className="h-8 w-8 text-lime-500" /> Assets
          </h1>
          <p className="text-slate-500 mt-1">Manage drones and equipment, and group them into kits.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="assets-scope-toggle">
            <button data-testid="scope-assets-btn" onClick={() => setScope("assets")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${scope === "assets" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><Boxes className="h-4 w-4" /> All Assets</button>
            <button data-testid="scope-groups-btn" onClick={() => setScope("groups")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${scope === "groups" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><FolderTree className="h-4 w-4" /> Groups</button>
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="assets-view-toggle">
            <button data-testid="view-list-btn" onClick={() => setLayout("list")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${layout === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><ListIcon className="h-4 w-4" /> List</button>
            <button data-testid="view-tiles-btn" onClick={() => setLayout("tiles")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${layout === "tiles" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><LayoutGrid className="h-4 w-4" /> Tiles</button>
          </div>
          {scope === "groups" && <Button variant="outline" data-testid="new-group-btn" onClick={openNewGroup}><Layers className="h-4 w-4 mr-1.5" /> New Group</Button>}
          <Button variant="outline" data-testid="asset-tracking-btn" onClick={() => navigate("/tracking")}><Radar className="h-4 w-4 mr-1.5" /> Asset Tracking</Button>
          <Button data-testid="new-asset-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1.5" /> New Asset</Button>
        </div>
      </div>

      {scope === "assets" ? (
        layout === "list" ? (
          <div className="space-y-2" data-testid="assets-list">
            {assets.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No assets yet.</p>}
            {assets.map(renderRow)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="assets-grid">
            {assets.map(renderCard)}
          </div>
        )
      ) : (
        <div className="space-y-3" data-testid="assets-grouped">
          {allSections.map(({ group, items, ungrouped: isUngrouped }) => {
            const isOpen = !!expanded[group.id];
            return (
              <div key={group.id} data-testid={`group-section-${group.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3">
                  <button onClick={() => toggleExpand(group.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`group-toggle-${group.id}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                    <div className="h-9 w-9 rounded-lg bg-lime-100 flex items-center justify-center shrink-0 overflow-hidden">
                      {group.image_path ? <img src={imgUrl(group.image_path)} alt="" className="h-full w-full object-cover" /> : <Layers className="h-4 w-4 text-lime-600" />}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading font-semibold text-slate-900 truncate">{group.name} <span className="text-slate-400 font-normal text-sm">· {items.length}</span></h3>
                      {group.description && <p className="text-xs text-slate-500 truncate">{group.description}</p>}
                    </div>
                  </button>
                  {!isUngrouped && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-slate-900" data-testid={`edit-group-${group.id}`} onClick={() => openEditGroup(group)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`delete-group-${group.id}`} onClick={() => removeGroup(group)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
                {isOpen && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/50" data-testid={`group-body-${group.id}`}>
                    {items.length === 0 ? (
                      <p className="text-sm text-slate-400">{isUngrouped ? "Everything is grouped." : "No assets in this group yet. Edit an asset and set its group."}</p>
                    ) : layout === "list" ? (
                      <div className="space-y-2">{items.map(renderRow)}</div>
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{items.map(renderCard)}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="asset-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{form.id ? "Edit Asset" : "New Asset"}</DialogTitle><DialogDescription>Add drone or equipment details.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
              <Input data-testid="asset-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="e.g. Agras T40 #1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
                <Select value={form.asset_type || "Drone"} onValueChange={(v) => set("asset_type", v)}><SelectTrigger className="mt-1.5" data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Model</Label>
                <Input value={form.model} onChange={(e) => set("model", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Registration</Label>
                <Input value={form.registration} onChange={(e) => set("registration", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Tracking code (ICAO24 / Mode-S hex)</Label>
              <Input data-testid="asset-icao24-input" value={form.icao24 || ""} onChange={(e) => set("icao24", e.target.value)} className="mt-1.5" placeholder="e.g. 7C6C6E — used for live flight tracking" />
              <p className="text-[11px] text-slate-400 mt-1">Optional. The aircraft's 6-character Mode-S hex, used by Asset Tracking to fetch its live position.</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Serial No.</Label>
                <Input value={form.serial_no} onChange={(e) => set("serial_no", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Purchase Date</Label>
                <Input type="date" value={form.purchase_date || ""} onChange={(e) => set("purchase_date", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Flight Hours</Label>
                <Input type="number" value={form.flight_hours} onChange={(e) => set("flight_hours", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Battery Count</Label>
                <Input type="number" value={form.battery_count} onChange={(e) => set("battery_count", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Group / Kit</Label>
              <Select value={form.group_id || "none"} onValueChange={(v) => set("group_id", v === "none" ? null : v)}>
                <SelectTrigger className="mt-1.5" data-testid="asset-group-select"><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ungrouped</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-lime-600 mb-3">Maintenance & compliance</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Service every (hrs)</Label>
                  <Input type="number" data-testid="asset-interval-hours" value={form.maintenance_interval_hours} onChange={(e) => set("maintenance_interval_hours", e.target.value)} className="mt-1.5" placeholder="e.g. 100" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Service every (days)</Label>
                  <Input type="number" data-testid="asset-interval-days" value={form.maintenance_interval_days} onChange={(e) => set("maintenance_interval_days", e.target.value)} className="mt-1.5" placeholder="e.g. 365" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Last service date</Label>
                  <Input type="date" value={form.last_service_date || ""} onChange={(e) => set("last_service_date", e.target.value)} className="mt-1.5" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Hours at last service</Label>
                  <Input type="number" value={form.last_service_hours} onChange={(e) => set("last_service_hours", e.target.value)} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Airworthiness expiry</Label>
                  <Input type="date" value={form.airworthiness_expiry || ""} onChange={(e) => set("airworthiness_expiry", e.target.value)} className="mt-1.5" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Cycles</Label>
                  <Input type="number" value={form.cycles} onChange={(e) => set("cycles", e.target.value)} className="mt-1.5" /></div>
              </div>
              <div className="mt-3"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Maintenance controller</Label>
                <Input value={form.maintenance_controller} onChange={(e) => set("maintenance_controller", e.target.value)} className="mt-1.5" placeholder="Required for RPA under ReOC" /></div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-lime-600 mb-3">Hire rates &amp; fees ($/hr)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Wet rate (incl. fuel)</Label>
                  <Input type="number" step="0.01" data-testid="asset-wet-rate" value={form.wet_rate ?? ""} onChange={(e) => set("wet_rate", e.target.value)} className="mt-1.5" placeholder="e.g. 1250" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Dry rate (excl. fuel)</Label>
                  <Input type="number" step="0.01" data-testid="asset-dry-rate" value={form.dry_rate ?? ""} onChange={(e) => set("dry_rate", e.target.value)} className="mt-1.5" placeholder="e.g. 950" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Student rate</Label>
                  <Input type="number" step="0.01" data-testid="asset-student-rate" value={form.student_rate ?? ""} onChange={(e) => set("student_rate", e.target.value)} className="mt-1.5" placeholder="e.g. 1250" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Landing fee (per landing)</Label>
                  <Input type="number" step="0.01" data-testid="asset-landing-fee" value={form.landing_fee ?? ""} onChange={(e) => set("landing_fee", e.target.value)} className="mt-1.5" placeholder="e.g. 45" /></div>
              </div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Main photo</Label>
              <Input data-testid="asset-image-input" type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} className="mt-1.5" />
              {uploading && <p className="text-xs text-slate-400 mt-1">Uploading…</p>}
              {form.image_path && <p className="text-xs text-lime-600 mt-1">Main image attached ✓</p>}</div>
            <MultiImageUploader value={form.images || []} onChange={(imgs) => set("images", imgs)} label="Additional photos" testid="asset-images" />
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5" rows={2} /></div>
            <Button data-testid="save-asset-btn" onClick={save} disabled={saving} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">{saving ? "Saving…" : "Save Asset"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="max-w-md" data-testid="group-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{groupForm.id ? "Edit Group" : "New Group"}</DialogTitle><DialogDescription>A group / kit holds related assets (e.g. "Truck 1 Kit").</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Group name</Label>
              <Input data-testid="group-name-input" value={groupForm.name} onChange={(e) => setG("name", e.target.value)} className="mt-1.5" placeholder="e.g. Truck 1 Kit" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
              <Textarea value={groupForm.description} onChange={(e) => setG("description", e.target.value)} className="mt-1.5" rows={2} /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Photo (optional)</Label>
              <Input type="file" accept="image/*" onChange={(e) => groupUpload(e.target.files[0])} className="mt-1.5" />
              {groupForm.image_path && <p className="text-xs text-lime-600 mt-1">Image attached ✓</p>}</div>
            <Button data-testid="save-group-btn" onClick={saveGroup} disabled={savingGroup} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">{savingGroup ? "Saving…" : "Save Group"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
